import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { getPostBySlug, getAllPosts, getPostImageUrl } from '../lib/blog';
import type { BlogPost, BlogPostMetadata } from '../types/blog';
import { Calendar, Clock, Tag, ArrowLeft } from 'lucide-react';

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [allPosts, setAllPosts] = useState<BlogPostMetadata[]>([]);
  const [nextPosts, setNextPosts] = useState<BlogPostMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const loadPost = async () => {
      if (!slug) {
        setError(true);
        setLoading(false);
        return;
      }

      try {
        const blogPost = await getPostBySlug(slug);
        if (blogPost) {
          setPost(blogPost);
          // Update page title
          document.title = `${blogPost.title} | Blogg - Beredskapshubben`;

          const canonicalUrl = `https://blogg.beredskapshubben.se/post/${blogPost.slug}`;
          let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
          if (!canonical) {
            canonical = document.createElement('link');
            canonical.rel = 'canonical';
            document.head.appendChild(canonical);
          }
          canonical.href = canonicalUrl;

          // Add meta tags for SEO
          const metaDescription = document.querySelector('meta[name="description"]');
          if (metaDescription) {
            metaDescription.setAttribute('content', blogPost.description);
          } else {
            const meta = document.createElement('meta');
            meta.name = 'description';
            meta.content = blogPost.description;
            document.head.appendChild(meta);
          }

          const metaKeywords = document.querySelector('meta[name="keywords"]');
          const keywords = blogPost.tags.join(', ');
          if (metaKeywords) {
            metaKeywords.setAttribute('content', keywords);
          } else {
            const meta = document.createElement('meta');
            meta.name = 'keywords';
            meta.content = keywords;
            document.head.appendChild(meta);
          }

          // Get all posts for sidebar and next posts
          const allPosts = await getAllPosts();
          setAllPosts(allPosts);
          // Sort by published date descending (newest first)
          const sortedPosts = [...allPosts].sort((a, b) => 
            new Date(b.publishedDate).getTime() - new Date(a.publishedDate).getTime()
          );
          const currentIndex = sortedPosts.findIndex(p => p.slug === slug);
          if (currentIndex !== -1) {
            const next = [];
            
            // Try to get 2 newer posts first (lower index = newer)
            if (currentIndex > 0) {
              next.push(sortedPosts[currentIndex - 1]);
            }
            if (currentIndex > 1) {
              next.push(sortedPosts[currentIndex - 2]);
            }
            
            // Fill with older posts if we don't have 2 yet
            if (next.length < 2 && currentIndex + 1 < sortedPosts.length) {
              next.push(sortedPosts[currentIndex + 1]);
            }
            if (next.length < 2 && currentIndex + 2 < sortedPosts.length) {
              next.push(sortedPosts[currentIndex + 2]);
            }
            
            setNextPosts(next);
          }
        } else {
          setError(true);
        }
      } catch (err) {
        console.error('Error loading post:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    loadPost();
  }, [slug]);

  useEffect(() => {
    // Ensure page is scrolled to top when opening an article
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [slug]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('sv-SE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-12">
        <div className="flex items-center justify-center">
          <div className="text-center">
            <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-primary border-r-transparent"></div>
            <p className="text-muted-foreground">Laddar artikel...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-12">
        <div className="text-center">
          <h1 className="mb-4 text-4xl font-bold text-foreground">Artikeln hittades inte</h1>
          <p className="mb-8 text-muted-foreground">
            Tyvärr kunde vi inte hitta artikeln du letar efter.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            <ArrowLeft className="h-4 w-4" />
            Tillbaka till startsidan
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-screen-xl px-4 py-12">
      <div className="flex flex-col lg:flex-row gap-8">
        {/* Main Content */}
        <article className="flex-1 max-w-4xl">
      {/* Schema.org structured data for SEO */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "headline": post.title,
          "description": post.description,
          "image": post.coverImage ? `https://blogg.beredskapshubben.se${post.coverImage}` : undefined,
          "author": {
            "@type": "Organization",
            "name": post.author || "Beredskapshubben"
          },
          "publisher": {
            "@type": "Organization",
            "name": "Beredskapshubben",
            "logo": {
              "@type": "ImageObject",
              "url": "https://blogg.beredskapshubben.se/icons/logo.svg"
            }
          },
          "datePublished": post.publishedDate,
          "dateModified": post.updatedDate || post.publishedDate,
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": `https://blogg.beredskapshubben.se/post/${post.slug}`
          },
          "keywords": post.tags.join(", ")
        })}
      </script>

      {/* Back button */}
      <Link
        to="/"
        className="mb-8 inline-flex items-center gap-2 text-sm text-[#e3ded5] transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Tillbaka till artiklar
      </Link>

      {/* Header */}
      <header className="mb-8">
        {/* Cover image */}
        {post.coverImage && (
          <div className="mb-6 overflow-hidden rounded-lg">
            <img
              src={post.coverImage}
              alt={post.title}
              className="w-full object-cover"
            />
          </div>
        )}

        {post.featured && (
          <div className="mb-4">
            <span className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-[#ed8b00]">
              Utvald artikel
            </span>
          </div>
        )}

        <h1 className="mb-4 text-4xl font-bold tracking-tight text-[#e3ded5] sm:text-5xl">
          {post.title}
        </h1>

        <p className="mb-6 text-xl text-[#e3ded5]">{post.description}</p>

        {/* Tags */}
        <div className="mb-6 flex flex-wrap gap-2">
          {post.tags.map((tag) => (
            <Link
              key={tag}
              to={`/tag/${encodeURIComponent(tag)}`}
              className="inline-flex items-center gap-1 rounded-md bg-secondary px-3 py-1 text-sm font-medium text-secondary-foreground transition-colors hover:bg-secondary/80"
            >
              <Tag className="h-3 w-3" />
              {tag}
            </Link>
          ))}
        </div>

        {/* Meta info */}
        <div className="flex flex-wrap items-center gap-4 border-b border-border pb-6 text-sm text-[#e3ded5]">
          {post.author && (
            <div>
              Av: <span className="font-medium text-[#e3ded5]">{post.author}</span>
            </div>
          )}
          <div className="flex items-center gap-1">
            <Calendar className="h-4 w-4" />
            <time dateTime={post.publishedDate}>{formatDate(post.publishedDate)}</time>
          </div>
          {post.updatedDate && post.updatedDate !== post.publishedDate && (
            <div className="flex items-center gap-1">
              Uppdaterad: <time dateTime={post.updatedDate}>{formatDate(post.updatedDate)}</time>
            </div>
          )}
          <div className="flex items-center gap-1">
            <Clock className="h-4 w-4" />
            <span>5 min läsning</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="prose prose-sm text-base dark:prose-invert max-w-none">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeSanitize]}
          components={{
            img: ({ node: _node, ...props }) => {
              const imgSrc = props.src?.startsWith('http') ? props.src : getPostImageUrl(post.slug, props.src ?? '');
              return (
                <a 
                  href={imgSrc} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block mx-auto md:inline-block md:mx-0 md:ml-6 max-w-[80%] md:max-w-[40%] cursor-pointer !my-0"
                >
                  <img
                    {...props}
                    className="rounded-lg w-full !my-0 border border-[#ed8b00]/60"
                    loading="lazy"
                    src={imgSrc}
                  />
                </a>
              );
            },
            h1: ({ node: _node, ...props }) => <h1 {...props} className={`text-[#e3ded5] ${props.className ?? ''}`} />,
            h2: ({ node: _node, ...props }) => <h2 {...props} className={`text-[#e3ded5] ${props.className ?? ''}`} />,
            h3: ({ node: _node, ...props }) => <h3 {...props} className={`text-[#e3ded5] ${props.className ?? ''}`} />,
            h4: ({ node: _node, ...props }) => <h4 {...props} className={`text-[#e3ded5] ${props.className ?? ''}`} />,
            h5: ({ node: _node, ...props }) => <h5 {...props} className={`text-[#e3ded5] ${props.className ?? ''}`} />,
            h6: ({ node: _node, ...props }) => <h6 {...props} className={`text-[#e3ded5] ${props.className ?? ''}`} />,
          }}
        >
          {post.content}
        </ReactMarkdown>
      </div>

      {/* Footer */}
      <footer className="mt-12 border-t border-border pt-8">
        <div className="rounded-lg bg-[#282014]/90 backdrop-blur-sm border border-border/50 p-6">
          <h3 className="mb-2 text-lg font-semibold text-[#e3ded5]">Är du redo?</h3>
          <p className="mb-4 text-[#e3ded5]">
            Börja din beredskapresa på beredskapshubben.se och förbered dig tillsammans med familj och grannar.
          </p>
          <style>{`
            @keyframes glow-pulse {
              0%, 100% { box-shadow: 0 0 12px rgba(237,139,0,0.5); }
              50% { box-shadow: 0 0 20px rgba(237,139,0,0.8); }
            }
            .glow-pulse-btn {
              animation: glow-pulse 3s ease-in-out infinite;
            }
          `}</style>
          <a
            href="https://beredskapshubben.se"
            target="_blank"
            rel="noopener noreferrer"
            className="glow-pulse-btn inline-flex items-center gap-3 rounded-lg bg-[#1a1a1a]/80 backdrop-blur-sm border-2 border-[#ed8b00] px-6 py-3 text-base font-semibold text-[#e3ded5] transition-all hover:bg-[#2a2a2a]/90 hover:border-[#ffa726]"
          >
            <img src="/icons/logo.svg" alt="Beredskapshubben" className="h-6 w-6" />
            Till Beredskapshubben
          </a>
        </div>

        {/* Read more section */}
        {nextPosts.length > 0 && (
          <div className="mt-8">
            <h3 className="mb-4 text-xl font-semibold text-[#e3ded5]">Läs mer</h3>
            <div className="grid gap-6 md:grid-cols-2">
              {nextPosts.map((nextPost) => (
                <Link
                  key={nextPost.slug}
                  to={`/post/${nextPost.slug}`}
                  className="group rounded-lg border border-border bg-card p-4 transition-colors hover:bg-muted/50"
                >
                  {nextPost.coverImage && (
                    <div className="mb-3 overflow-hidden rounded-md">
                      <img
                        src={nextPost.coverImage}
                        alt={nextPost.title}
                        className="h-40 w-full object-cover transition-transform group-hover:scale-105"
                      />
                    </div>
                  )}
                  <h4 className="mb-2 font-semibold text-[#e3ded5] group-hover:text-primary">
                    {nextPost.title}
                  </h4>
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {nextPost.description}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </footer>
    </article>

        {/* Sidebar - Desktop only */}
        <aside className="hidden lg:block lg:w-72 flex-shrink-0 pl-8">
          <div className="sticky top-6 space-y-8">
            {/* CTA card */}
            <div className="rounded-lg bg-[#282014]/90 backdrop-blur-sm border border-border/50 p-5">
              <h3 className="mb-2 text-base font-semibold text-[#e3ded5]">Är du redo?</h3>
              <p className="mb-4 text-sm text-[#e3ded5]">
                Börja din beredskapresa på beredskapshubben.se och förbered dig tillsammans med familj och grannar.
              </p>
              <a
                href="https://beredskapshubben.se"
                target="_blank"
                rel="noopener noreferrer"
                className="glow-pulse-btn inline-flex items-center gap-2 rounded-lg bg-[#1a1a1a]/80 backdrop-blur-sm border-2 border-[#ed8b00] px-4 py-2 text-sm font-semibold text-[#e3ded5] transition-all hover:bg-[#2a2a2a]/90 hover:border-[#ffa726]"
              >
                <img src="/icons/logo.svg" alt="Beredskapshubben" className="h-5 w-5" />
                Till Beredskapshubben
              </a>
            </div>

            {/* Tags */}
            <div className="mb-8">
              <h3 className="mb-4 text-lg font-semibold text-foreground">
                Taggar
              </h3>
              <div className="flex flex-wrap gap-2">
                {[...new Set(allPosts.flatMap(p => p.tags))].sort().map((tag) => (
                  <Link
                    key={tag}
                    to={`/tag/${encodeURIComponent(tag)}`}
                    className="inline-flex items-center gap-1 rounded-md bg-secondary px-3 py-1 text-sm font-medium text-secondary-foreground transition-colors hover:bg-secondary/80"
                  >
                    <Tag className="h-3 w-3" />
                    {tag}
                  </Link>
                ))}
              </div>
            </div>

            {/* Senaste inlägg */}
            <h3 className="mb-4 text-lg font-semibold text-foreground">
              Senaste inlägg
            </h3>
            <div className="space-y-2">
              {allPosts.slice(0, 20).map((p) => (
                <Link
                  key={p.slug}
                  to={`/post/${p.slug}`}
                  className="block rounded-md bg-card p-3 text-sm hover:bg-accent transition-colors"
                >
                  <div className="font-medium text-foreground line-clamp-2">
                    {p.title}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {new Date(p.publishedDate).toLocaleDateString('sv-SE', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
